import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, CalendarDays, Clock3, MapPin, CheckCircle2, Circle, XCircle,
  ArrowRight, ArrowLeft, ShieldCheck, Sparkles, Plus, Pencil,
  Info, PartyPopper,
} from "lucide-react";
import CONFIG, { getUserCity } from "../../../config";
import { BEDROOM_WARDROBE_CLEANING_SCOPE } from "../../../config/pricing";
import { isUserLoggedIn, getAuthMobile, getAuthToken } from "../../common/Header";
import CurrentLocationDetector from "../../common/CurrentLocationDetector";
import CurrentLocationCard from "../../common/CurrentLocationCard";
import bedroomCleaningImg from "../../images/stage_01/bedroom_cleaning.png";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const HERO_IMAGE   = bedroomCleaningImg;
const REDIRECT_PATH = "/service/special-service/bedroom-wardrobe-cleaning";
// Sentinel selectedAddressId value meaning "use the detected current
// location" rather than one of the customer's saved addresses.
const CURRENT_LOCATION_ID = "__current_location__";
// City is a fixed, config-driven selection (CONFIG.ACTIVE_CITIES/CITIES) —
// never free text — so a saved/booking address.city always matches the
// canonical slug partner-matching compares against (see the city-alias
// bug this fixed on the backend: special_service_assign_common.py).
const CITY_OPTIONS = (CONFIG.ACTIVE_CITIES || []).map((slug) => ({
  value: slug, label: CONFIG.CITIES?.[slug]?.label || slug,
}));

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

// ─────────────────────────────────────────────────────────────────────────────
// STATIC CONTENT
// ─────────────────────────────────────────────────────────────────────────────
const STEPS = [
  { key: "property", label: "Property Details", sub: "Select your bedroom/wardrobe size" },
  { key: "included", label: "What's Included",  sub: "Expand to see details"            },
  { key: "schedule", label: "Schedule",       sub: "Date & time"                         },
  { key: "review",   label: "Summary",        sub: "Address, confirm & pay"              },
];

// Pricing (one price per category+size tier) is admin-editable in
// pricing.js: SPECIAL_SERVICES_PRICING["bedroom-wardrobe-cleaning"].sizes.
const BEDROOM_CONFIG = CONFIG.SERVICE_CONFIG?.["special-services"]?.services?.["bedroom-wardrobe-cleaning"] || {};
const CATEGORIES = BEDROOM_CONFIG.categories || [];
const BOOKING_WINDOW_DAYS = BEDROOM_CONFIG.bookingWindowDays || 6;

const TRUST_BULLETS = [
  { icon: Users,        text: "Verified Professionals"   },
  { icon: Sparkles,     text: "Equipment Included"        },
  { icon: ShieldCheck,  text: "Safe & Secure"              },
  { icon: CheckCircle2, text: "Satisfaction Guaranteed"   },
];

// Visit duration is driven by the bedroom-range tier picked in Step 1, not
// a separate user choice — 1-3 Bedrooms + Wardrobe is a 4-hour visit, 4-6
// Bedrooms + Wardrobe is 6 hours (same for Standard and Premium). Time
// slots are the same 4hr/6hr windows used across Rapid/Deep Cleaning,
// Kitchen & Utensil Cleaning and Move-In/Move-Out Cleaning's Schedule
// steps.
const DURATION_BY_AREA_RANGE = {
  "1-3bhk": "4hr",
  "4-6bhk": "6hr",
};
const DURATION_LABELS = { "4hr": "4 Hours", "6hr": "6 Hours" };
const TIME_SLOTS_BY_DURATION = {
  "4hr": [
    { id: "4hr_0700_1100", label: "7:00 AM – 11:00 AM" },
    { id: "4hr_1200_1600", label: "12:00 PM – 4:00 PM" },
    { id: "4hr_1700_2100", label: "5:00 PM – 9:00 PM"  },
  ],
  "6hr": [
    { id: "6hr_0700_1300", label: "7:00 AM – 1:00 PM" },
    { id: "6hr_1400_2000", label: "2:00 PM – 8:00 PM" },
  ],
};

const ADDR_LABEL_ICONS = { Home: "🏠", Work: "🏢", Office: "🏢", Other: "📍" };

// Booking starts from tomorrow (no same-day slots) and stays open for
// BOOKING_WINDOW_DAYS days from there.
const buildDateChips = () => {
  const chips = [];
  for (let i = 1; i <= BOOKING_WINDOW_DAYS; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const label = i === 1 ? "Tomorrow"
      : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
    chips.push({ iso, label });
  }
  return chips;
};

const formatDateLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
};

// Lightweight, dependency-free confetti burst for the confirmation screen.
const CONFETTI_COLORS = ["#7c3aed", "#f59e0b", "#22c55e", "#3b82f6", "#0d9488", "#ec4899"];
const buildConfettiPieces = () =>
  Array.from({ length: 28 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.6,
    duration: 1.8 + Math.random() * 1.2,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    rotate: Math.random() * 360,
  }));

// ─────────────────────────────────────────────────────────────────────────────
const BedroomWardrobeCleaningPage = () => {
  const navigate = useNavigate();
  const mobile   = getAuthMobile();

  const [step, setStep] = useState("property");
  const stepIndex = STEPS.findIndex((s) => s.key === step);

  // ── Step 1: Property details (category + bedroom size) ───────────────────
  const [category, setCategory] = useState("");
  const [areaRange, setAreaRange] = useState("");
  const [city]      = useState(getUserCity() || "bengaluru");
  const SIZES = (BEDROOM_CONFIG.sizes || {})[category] || [];
  const categoryMeta = CATEGORIES.find((c) => c.value === category) || null;
  const areaMeta = SIZES.find((s) => s.value === areaRange) || null;
  const basePrice = areaMeta?.price || 0;

  // Switching category changes that category's price list — any size
  // already picked under the old category no longer applies.
  const handleSelectCategory = (value) => {
    if (value === category) return;
    setCategory(value);
    setAreaRange("");
  };

  // ── Step 2: What's Included / Excluded — driven by the Step 1 category ───
  const scope = BEDROOM_WARDROBE_CLEANING_SCOPE[category] || null;
  const hasScopeContent = Boolean(
    scope && (scope.included.length || scope.excluded.length || scope.customerNotes.length)
  );

  // ── Step 3: Schedule + Address ────────────────────────────────────────────
  const dateChips = useMemo(() => buildDateChips(), []);
  const [selectedDate,   setSelectedDate]   = useState("");
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [slot,            setSlot]           = useState("");

  // Duration is implied by the bedroom-count tier chosen in Step 1.
  const duration        = DURATION_BY_AREA_RANGE[areaRange] || "";
  const durationLabel   = DURATION_LABELS[duration] || "";
  const timeSlotChoices = TIME_SLOTS_BY_DURATION[duration] || [];
  const sessionMeta     = timeSlotChoices.find((t) => t.id === slot) || null;

  const [addresses,        setAddresses]        = useState([]);
  const [loadingAddresses, setLoadingAddresses]  = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  // Detected-and-editable GPS address — kept separate from the saved-
  // addresses list (not persisted to the profile); selecting it vs. a
  // saved address is just which one selectedAddressId currently points at.
  const [currentLocationAddr, setCurrentLocationAddr] = useState(null);
  const [showAddForm,      setShowAddForm]       = useState(false);
  const [savingAddr,       setSavingAddr]        = useState(false);
  const [addrFormError,    setAddrFormError]     = useState("");
  const [instructions,     setInstructions]      = useState("");
  const [newAddr, setNewAddr] = useState({
    label: "Home", line1: "", area: "", city, state: "", pincode: "", map_url: "",
  });

  useEffect(() => {
    if (!mobile) return;
    let cancelled = false;
    (async () => {
      setLoadingAddresses(true);
      try {
        const res  = await fetch(`${API_BASE_URL}/api/user/user-addresses/${encodeURIComponent(mobile)}/`, { headers: authHeaders() });
        const data = await res.json();
        const list = Array.isArray(data?.data) ? data.data : [];
        if (!cancelled) {
          setAddresses(list);
          if (list.length) setSelectedAddressId(list[0].id);
        }
      } catch { /* saved addresses are optional — user can still add one manually */ }
      finally { if (!cancelled) setLoadingAddresses(false); }
    })();
    return () => { cancelled = true; };
  }, [mobile]);

  const newAddrAreaOptions = useMemo(() => {
    const entry = CONFIG.AREA_OPTIONS?.[newAddr.city] || CONFIG.AREA_OPTIONS?.[newAddr.city?.toLowerCase()];
    return entry?.areas || [];
  }, [newAddr.city]);

  // Reverse-geocodes the detected GPS point into a full, editable address
  // (see CurrentLocationDetector.js) and makes it its own selectable
  // option alongside the saved addresses below — NOT funneled into the
  // "Add New Address" form, and not persisted to the profile, since it's
  // only relevant to this one booking.
  const handleLocationDetected = (addr) => {
    setCurrentLocationAddr(addr);
    setSelectedAddressId(CURRENT_LOCATION_ID);
    setShowAddForm(false);
  };

  const handleAddAddress = async () => {
    setAddrFormError("");
    if (!newAddr.line1.trim() || !newAddr.city || !newAddr.area || !newAddr.pincode.trim()) {
      setAddrFormError("Please fill in the house/flat details, city, area and pincode.");
      return;
    }
    if (addresses.length >= 3) {
      setAddrFormError("You already have 3 saved addresses — remove one from your profile first.");
      return;
    }
    if (!mobile) {
      navigate("/user/user-login", { state: { redirectTo: REDIRECT_PATH } });
      return;
    }
    const entry = {
      id: `addr-${Date.now()}`, label: newAddr.label, line1: newAddr.line1.trim(),
      area: newAddr.area, city: newAddr.city, state: newAddr.state.trim(),
      pincode: newAddr.pincode.trim(), map_url: newAddr.map_url || "",
    };
    const updated = [...addresses, entry];
    setSavingAddr(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/user/user-addresses/${encodeURIComponent(mobile)}/`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify({ addresses: updated }),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) throw new Error(data?.message || "Failed to save address.");
      const saved = Array.isArray(data?.data) ? data.data : updated;
      setAddresses(saved);
      const savedEntry = saved.find((a) => a.line1 === entry.line1 && a.pincode === entry.pincode) || saved[saved.length - 1];
      setSelectedAddressId(savedEntry?.id || entry.id);
      setShowAddForm(false);
      setNewAddr({ label: "Home", line1: "", area: "", city, state: "", pincode: "", map_url: "" });
    } catch (e) {
      setAddrFormError(e.message || "Failed to save address.");
    } finally {
      setSavingAddr(false);
    }
  };

  const selectedAddress = selectedAddressId === CURRENT_LOCATION_ID
    ? currentLocationAddr
    : addresses.find((a) => a.id === selectedAddressId) || null;

  // ── Pricing ────────────────────────────────────────────────────────────
  const CHARGES = CONFIG.CHECKOUT_CHARGES ?? { GST_PERCENT: 18 };
  const PARTIAL_THRESHOLD = CONFIG.PARTIAL_PAYMENT_THRESHOLD ?? 1000;
  const subtotal  = basePrice;
  const gst       = Math.round(subtotal * (CHARGES.GST_PERCENT / 100));
  const total     = subtotal + gst;
  const canPayLater = subtotal >= PARTIAL_THRESHOLD;

  const [paymentOption, setPaymentOption] = useState("pay_now");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [placing,       setPlacing]       = useState(false);
  const [placeError,    setPlaceError]    = useState("");
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  useEffect(() => { if (!canPayLater) setPaymentOption("pay_now"); }, [canPayLater]);

  // ── Step validation / navigation ──────────────────────────────────────────
  const canContinue = {
    property: Boolean(category && areaRange),
    included: true,
    // Address is picked on the Summary step now, not here.
    // sessionMeta (not just slot) — re-validates the slot still belongs to
    // the current duration bucket if the customer went back and picked a
    // different bedroom-count tier after already choosing a slot.
    schedule: Boolean(selectedDate && slot && sessionMeta),
  };

  const goNext = () => {
    const idx = STEPS.findIndex((s) => s.key === step);
    if (!canContinue[step]) return;
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].key);
  };
  const goBack = () => {
    const idx = STEPS.findIndex((s) => s.key === step);
    if (idx > 0) setStep(STEPS[idx - 1].key); else navigate(-1);
  };

  // ── Place order ────────────────────────────────────────────────────────────
  // Bedroom & Wardrobe Cleaning bookings are stored in their own collection
  // (special_service_bedroomWardrobeCleaning) via a dedicated endpoint, not
  // the generic cart place-order pipeline — see book_bedroom_wardrobe_cleaning()
  // / bedroom_wardrobe_cleaning_booking_detail() in the backend.
  const handlePlaceOrder = async () => {
    if (!agreedToTerms || !selectedAddressId || placing) return;
    if (!isUserLoggedIn() || !mobile) {
      navigate("/user/user-login", { state: { redirectTo: REDIRECT_PATH } });
      return;
    }

    const item = {
      id: "bedroom_wardrobe_cleaning", service_id: "bedroom_wardrobe_cleaning",
      title: `Bedroom & Wardrobe Cleaning — ${categoryMeta?.label} — ${areaMeta?.label}`,
      name:  `Bedroom & Wardrobe Cleaning — ${categoryMeta?.label} — ${areaMeta?.label}`,
      price: subtotal, qty: 1,
      image: HERO_IMAGE,
    };

    const bookingPayload = {
      mobile,
      property_details: {
        category, category_label: categoryMeta?.label,
        area_range: areaRange, area_range_label: areaMeta?.label,
        price: basePrice, city,
      },
      addons: [],
      schedule: {
        date: selectedDate, date_label: formatDateLabel(selectedDate),
        duration, duration_label: durationLabel,
        slot, slot_label: sessionMeta?.label, time_range: sessionMeta?.label,
      },
      address: selectedAddress || null,
      instructions,
      pricing: { subtotal, gst, total },
      payment_option: paymentOption,
      // Display-only for PaymentPage.js's shared order-summary UI:
      items: [item],
      delivery_address: selectedAddress || null,
      total_amount: total,
    };

    setPlacing(true);
    setPlaceError("");

    if (paymentOption === "pay_after_work") {
      try {
        const res  = await fetch(`${API_BASE_URL}/api/special-service/bedroom-wardrobe-cleaning/book/`, {
          method: "POST", credentials: "include", headers: authHeaders(), body: JSON.stringify(bookingPayload),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.message || "Failed to place booking.");
        setConfirmedBooking({
          orderId: data?.data?.process_id || "",
          dateLabel: formatDateLabel(selectedDate),
          slot: sessionMeta ? `${durationLabel} · ${sessionMeta.label}` : "",
          address: selectedAddress,
        });
        setStep("confirmed");
      } catch (e) {
        setPlaceError(e.message || "Failed to place booking. Please try again.");
      } finally {
        setPlacing(false);
      }
      return;
    }

    try {
      const res  = await fetch(`${API_BASE_URL}/api/payment/create-order/`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ amount: total, mobile }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Could not initiate payment.");
      navigate("/payment", {
        state: {
          paymentOrder: data,
          orderPayload: { ...bookingPayload, __submit_endpoint: "/api/special-service/bedroom-wardrobe-cleaning/book/" },
          breakdown: { subtotal, discount: 0, gst, platformFee: 0, tip: 0, total },
        },
      });
    } catch (e) {
      setPlaceError(e.message || "Payment initiation failed. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  const confetti = useMemo(() => (step === "confirmed" ? buildConfettiPieces() : []), [step]);

  // ─────────────────────────────────────────────────────────────────────────
  if (step === "confirmed") {
    return (
      <div style={S.page}>
        <div style={{ ...S.container, maxWidth: "560px" }}>
          <div style={S.confirmCard}>
            <div style={S.confettiWrap}>
              {confetti.map((c) => (
                <span
                  key={c.id}
                  style={{
                    ...S.confettiPiece,
                    left: `${c.left}%`,
                    background: c.color,
                    animationDelay: `${c.delay}s`,
                    animationDuration: `${c.duration}s`,
                    transform: `rotate(${c.rotate}deg)`,
                  }}
                />
              ))}
            </div>
            <div style={S.confirmCheckWrap}>
              <div style={S.confirmCheck}><CheckCircle2 size={44} color="#fff" /></div>
            </div>
            <h2 style={S.confirmTitle}>Booking Confirmed! <PartyPopper size={22} color="#f59e0b" /></h2>
            <p style={S.confirmSub}>Your cleaning is scheduled</p>
            <div style={S.confirmOrderId}>Order ID: {confirmedBooking?.orderId || "—"}</div>

            <div style={S.confirmDetailRow}><CalendarDays size={16} color="#4b5563" /> {confirmedBooking?.dateLabel}, {confirmedBooking?.slot}</div>
            {confirmedBooking?.address && (
              <div style={S.confirmDetailRow}>
                <MapPin size={16} color="#4b5563" />
                {[confirmedBooking.address.line1, confirmedBooking.address.area, confirmedBooking.address.city].filter(Boolean).join(", ")}
              </div>
            )}

            <p style={S.confirmNote}>We've sent the details to your email &amp; SMS.</p>

            <div style={S.confirmActions}>
              <button type="button" style={S.confirmPrimaryBtn} onClick={() => navigate("/profile-dashboard", { state: { openBookings: true } })}>
                View My Bookings <ArrowRight size={16} />
              </button>
              <Link to="/services/special-services" style={S.confirmSecondaryBtn}>Back to Special Services</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span>{" "}
          <Link to="/services/special-services" style={S.breadcrumbLink}>Special Services</Link> <span>&gt;</span>{" "}
          <span>Bedroom &amp; Wardrobe Cleaning</span>
        </div>

        <div style={S.hero}>
          <div style={S.heroBadge}><Sparkles size={13} /> BEDROOM &amp; WARDROBE CLEANING</div>
          <h1 style={S.heroTitle}>Bedroom &amp; Wardrobe Cleaning</h1>
          <p style={S.heroText}>Dusting, wardrobe organizing, mattress &amp; upholstery care and more — trained teams, transparent pricing.</p>
        </div>

        {/* Step progress */}
        <div style={S.stepRowOuter}>
          <div style={S.stepRow}>
            {STEPS.map((s, i) => (
              <React.Fragment key={s.key}>
                <div style={S.stepItem}>
                  <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                    {i < stepIndex ? <CheckCircle2 size={13} /> : i + 1}
                  </div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                </div>
                {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
              </React.Fragment>
            ))}
          </div>
          <div style={S.stepSub}>{STEPS[stepIndex]?.sub}</div>
        </div>

        <button type="button" style={S.backBtn} onClick={goBack}>
          <ArrowLeft size={15} /> Back
        </button>

        {/* ── Step 1: Property Details (category + bedroom size) ─────────── */}
        {step === "property" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Choose your service category</h2>
            <p style={S.scheduleHeadSub}>Select Standard or Premium cleaning.</p>

            <div style={S.sizeGrid}>
              {CATEGORIES.map((c) => {
                const selected = category === c.value;
                return (
                  <button key={c.value} type="button" style={{ ...S.sizeCard, ...(selected ? S.sizeCardActive : {}) }} onClick={() => handleSelectCategory(c.value)}>
                    <div style={S.sizeCardHead}>
                      {selected ? <CheckCircle2 size={18} color="#7c3aed" /> : <Circle size={18} color="#d1d5db" />}
                      <span style={S.sizeCardLabel}>{c.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <h2 style={S.sectionTitle}>How many bedrooms &amp; wardrobes?</h2>
            <p style={S.scheduleHeadSub}>
              {category ? "Pick the size closest to your home — price updates automatically." : "Choose a category above first."}
            </p>

            <div style={S.sizeGrid}>
              {SIZES.map((s) => {
                const selected = areaRange === s.value;
                return (
                  <button key={s.value} type="button" style={{ ...S.sizeCard, ...(selected ? S.sizeCardActive : {}) }} onClick={() => setAreaRange(s.value)}>
                    <div style={S.sizeCardHead}>
                      {selected ? <CheckCircle2 size={18} color="#7c3aed" /> : <Circle size={18} color="#d1d5db" />}
                      <span style={S.sizeCardLabel}>{s.label}</span>
                    </div>
                    <div style={S.sizeCardPrice}>{fmt(s.price)}</div>
                  </button>
                );
              })}
            </div>

            <div style={S.trustStrip}>
              {TRUST_BULLETS.map(({ icon: Icon, text }) => (
                <div key={text} style={S.trustItem}><Icon size={16} color="#7c3aed" /> {text}</div>
              ))}
            </div>

            <button type="button" style={{ ...S.continueBtn, ...(!canContinue.property ? S.continueBtnDisabled : {}) }} disabled={!canContinue.property} onClick={goNext}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 2: What's Included / Excluded (by category) ───────────── */}
        {step === "included" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>{scope?.title || "What's Included"}</h2>
            <p style={S.scheduleHeadSub}>What's included and excluded for your selected category.</p>

            {hasScopeContent ? (
              <>
                <div style={S.doDontGrid}>
                  <div>
                    <div style={S.doDontColTitleInclude}><CheckCircle2 size={15} color="#7c3aed" /> Included</div>
                    <ul style={S.doDontList}>
                      {scope.included.map((p) => (
                        <li key={p} style={S.doDontItem}>
                          <CheckCircle2 size={13} color="#7c3aed" /> <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div style={S.doDontColTitleExclude}><XCircle size={15} color="#dc2626" /> Excluded</div>
                    <ul style={S.doDontList}>
                      {scope.excluded.map((p) => (
                        <li key={p} style={S.doDontItem}>
                          <XCircle size={13} color="#dc2626" /> <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {scope.customerNotes.length > 0 && (
                  <div style={S.notesBox}>
                    <div style={S.notesBoxTitle}><Info size={15} color="#7c3aed" /> Customer Responsibility &amp; Safety</div>
                    <ul style={S.notesList}>
                      {scope.customerNotes.map((n) => (
                        <li key={n} style={S.notesItem}>{n}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {BEDROOM_WARDROBE_CLEANING_SCOPE.finalDifference && (
                  <div style={S.notesBox}>
                    <div style={S.notesBoxTitle}><Sparkles size={15} color="#7c3aed" /> Standard vs Premium — Final Positioning</div>
                    <ul style={S.notesList}>
                      {BEDROOM_WARDROBE_CLEANING_SCOPE.finalDifference.map((n) => (
                        <li key={n} style={S.notesItem}>{n}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <p style={S.scheduleHeadSub}>
                {category ? "Detailed included/excluded list for this category is coming soon." : "Go back and choose Standard or Premium to see what's included."}
              </p>
            )}

            <button type="button" style={S.continueBtn} onClick={goNext}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 3: Schedule & Address ────────────────────────────────── */}
        {step === "schedule" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Select Date &amp; Time</h2>
            <p style={S.scheduleHeadSub}>Bookable from tomorrow, up to {BOOKING_WINDOW_DAYS} days ahead.</p>

            <div style={S.fieldGroup}>
              <div style={S.chipRow}>
                {dateChips.map((c) => (
                  <button key={c.iso} type="button" style={{ ...S.dateChip, ...(selectedDate === c.iso ? S.dateChipActive : {}) }}
                    onClick={() => { setSelectedDate(c.iso); setCustomDateOpen(false); }}>
                    {c.label}
                  </button>
                ))}
                <button type="button" style={{ ...S.dateChip, ...(customDateOpen ? S.dateChipActive : {}) }} onClick={() => setCustomDateOpen((v) => !v)}>
                  <CalendarDays size={14} /> Pick a Date
                </button>
              </div>
              {customDateOpen && (
                <input
                  type="date" style={S.dateInput}
                  min={dateChips[0]?.iso}
                  max={dateChips[dateChips.length - 1]?.iso}
                  value={!dateChips.some((c) => c.iso === selectedDate) ? selectedDate : ""}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              )}
            </div>

            {durationLabel && (
              <div style={S.durationBanner}>
                <Clock3 size={14} /> Visit duration: <strong>{durationLabel}</strong> (based on {areaMeta?.label})
              </div>
            )}

            <div style={S.fieldGroup}>
              <div style={S.fieldLabel}>Select Time Slot</div>
              <div style={S.slotGrid}>
                {timeSlotChoices.map((t) => (
                  <button key={t.id} type="button" style={{ ...S.slotChip, ...(slot === t.id ? S.slotChipActive : {}) }} onClick={() => setSlot(t.id)}>
                    <div style={{ ...S.slotChipLabel, ...(slot === t.id ? { color: "#fff" } : {}) }}>{t.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <button type="button" style={{ ...S.continueBtn, ...(!canContinue.schedule ? S.continueBtnDisabled : {}) }} disabled={!canContinue.schedule} onClick={goNext}>
              Continue to Summary <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 4: Summary — address, instructions, payment, terms ───── */}
        {step === "review" && (
          <div style={S.reviewLayout}>
            <div style={S.reviewLeft}>
              <div style={S.recapCard}>
                <div style={S.recapHead}>
                  <div style={S.recapTitle}><Sparkles size={16} color="#7c3aed" /> Property Details</div>
                  <button type="button" style={S.changeLink} onClick={() => setStep("property")}><Pencil size={12} /> Change</button>
                </div>
                <div style={S.recapText}>{categoryMeta?.label} · {areaMeta?.label} — {fmt(basePrice)}</div>
              </div>

              <div style={S.recapCard}>
                <div style={S.recapHead}>
                  <div style={S.recapTitle}><CalendarDays size={16} color="#7c3aed" /> Date &amp; Time</div>
                  <button type="button" style={S.changeLink} onClick={() => setStep("schedule")}><Pencil size={12} /> Change</button>
                </div>
                <div style={S.recapText}>{formatDateLabel(selectedDate)} · {durationLabel} · {sessionMeta?.label}</div>
              </div>

              <div style={S.recapCard}>
                <div style={S.recapHead}>
                  <div style={S.recapTitle}><MapPin size={16} color="#7c3aed" /> Service Address</div>
                </div>

                <CurrentLocationDetector onDetected={handleLocationDetected}
                  buttonStyle={S.locationBtn} errorStyle={S.errorText}
                  idleLabel="📍 Use my current location" />

                {currentLocationAddr && (
                  <div style={S.fieldGroup}>
                    <CurrentLocationCard
                      addr={currentLocationAddr}
                      onChange={(updated) => { setCurrentLocationAddr(updated); setSelectedAddressId(CURRENT_LOCATION_ID); }}
                      cardStyle={{
                        border: selectedAddressId === CURRENT_LOCATION_ID ? "2px solid #7c3aed" : "1.5px solid #e5e7eb",
                        borderRadius: "14px", padding: "14px",
                        background: selectedAddressId === CURRENT_LOCATION_ID ? "#f5f3ff" : "#fff",
                        display: "flex", flexDirection: "column", gap: "10px",
                      }}
                    />
                    {selectedAddressId !== CURRENT_LOCATION_ID && (
                      <button type="button" style={{ ...S.secondaryBtn, marginTop: "8px" }} onClick={() => setSelectedAddressId(CURRENT_LOCATION_ID)}>
                        Use this address
                      </button>
                    )}
                  </div>
                )}

                {!mobile ? (
                  <div style={S.stateBox}>
                    <Link to="/user/user-login" state={{ redirectTo: REDIRECT_PATH }} style={S.inlineLink}>Log in</Link> to use a saved address, or add one below.
                  </div>
                ) : loadingAddresses ? (
                  <div style={S.stateBox}>Loading your addresses…</div>
                ) : addresses.length > 0 && (
                  <div style={S.fieldGroup}>
                    <div style={S.fieldLabel}>Saved Addresses</div>
                    <div style={S.addressList}>
                      {addresses.map((addr) => {
                        const selected = selectedAddressId === addr.id;
                        return (
                          <button key={addr.id} type="button" style={{ ...S.addressRow, ...(selected ? S.addressRowActive : {}) }} onClick={() => setSelectedAddressId(addr.id)}>
                            {selected ? <CheckCircle2 size={18} color="#7c3aed" /> : <Circle size={18} color="#d1d5db" />}
                            <span style={S.addressIcon}>{ADDR_LABEL_ICONS[addr.label] || "📍"}</span>
                            <div style={S.addressBody}>
                              <div style={S.addressLabel}>{addr.label}</div>
                              <div style={S.addressText}>{[addr.line1, addr.area, addr.city].filter(Boolean).join(", ")}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!showAddForm ? (
                  <button type="button" style={S.addAddressBtn} onClick={() => setShowAddForm(true)}>
                    <Plus size={15} /> Add New Address
                  </button>
                ) : (
                  <div style={S.addAddressForm}>
                    <div style={S.fieldLabel}>New Address</div>
                    <div style={S.radioGrid3}>
                      {["Home", "Work", "Other"].map((l) => (
                        <button key={l} type="button" style={{ ...S.radioCard, ...(newAddr.label === l ? S.radioCardActive : {}) }} onClick={() => setNewAddr((p) => ({ ...p, label: l }))}>
                          {ADDR_LABEL_ICONS[l]} {l}
                        </button>
                      ))}
                    </div>
                    <input style={S.input} placeholder="House / Flat No., Building, Street" value={newAddr.line1}
                      onChange={(e) => setNewAddr((p) => ({ ...p, line1: e.target.value }))} />
                    <select style={S.select} value={newAddr.city} onChange={(e) => setNewAddr((p) => ({ ...p, city: e.target.value, area: "" }))}>
                      <option value="">Select city</option>
                      {CITY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <div style={S.formRow2}>
                      <select style={S.select} value={newAddr.area} onChange={(e) => setNewAddr((p) => ({ ...p, area: e.target.value }))}>
                        <option value="">Select area</option>
                        {newAddrAreaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <input style={S.input} placeholder="Pincode" value={newAddr.pincode}
                        onChange={(e) => setNewAddr((p) => ({ ...p, pincode: e.target.value }))} />
                    </div>
                    <input style={S.input} placeholder="State (optional)" value={newAddr.state}
                      onChange={(e) => setNewAddr((p) => ({ ...p, state: e.target.value }))} />
                    {addrFormError && <div style={S.errorText}>{addrFormError}</div>}
                    <div style={S.formActions}>
                      <button type="button" style={S.secondaryBtn} onClick={() => { setShowAddForm(false); setAddrFormError(""); }}>Cancel</button>
                      <button type="button" style={S.primaryBtnSm} onClick={handleAddAddress} disabled={savingAddr}>
                        {savingAddr ? "Saving…" : "Save Address"}
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ ...S.fieldGroup, marginBottom: 0 }}>
                  <div style={S.fieldLabel}>Special Instructions (optional)</div>
                  <textarea style={S.textarea} rows={3} placeholder="Gate code, parking info, areas to focus on…" value={instructions}
                    onChange={(e) => setInstructions(e.target.value)} />
                </div>
              </div>


              <div style={S.recapCard}>
                <div style={S.fieldLabel}>Payment</div>
                <div style={S.paymentOptions}>
                  <button type="button" style={{ ...S.payOptionBtn, ...(paymentOption === "pay_now" ? S.payOptionBtnActive : {}) }} onClick={() => setPaymentOption("pay_now")}>
                    {paymentOption === "pay_now" ? <CheckCircle2 size={16} color="#7c3aed" /> : <Circle size={16} color="#d1d5db" />}
                    <div>
                      <div style={S.payOptionTitle}>Pay Now</div>
                      <div style={S.payOptionSub}>Pay the full amount upfront</div>
                    </div>
                  </button>
                  <button type="button" disabled={!canPayLater}
                    style={{ ...S.payOptionBtn, ...(paymentOption === "pay_after_work" ? S.payOptionBtnActive : {}), ...(!canPayLater ? S.payOptionBtnDisabled : {}) }}
                    onClick={() => canPayLater && setPaymentOption("pay_after_work")}>
                    {paymentOption === "pay_after_work" ? <CheckCircle2 size={16} color="#7c3aed" /> : <Circle size={16} color="#d1d5db" />}
                    <div>
                      <div style={S.payOptionTitle}>Pay After Work</div>
                      <div style={S.payOptionSub}>{canPayLater ? "Pay once the job is done" : `Available above ${fmt(PARTIAL_THRESHOLD)}`}</div>
                    </div>
                  </button>
                </div>
              </div>

              {placeError && <div style={S.errorBox}>{placeError}</div>}

              <label style={S.termsWrap}>
                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} />
                <span>
                  I agree to the{" "}
                  <a href="/legal-information" style={S.inlineLink}>Terms &amp; Conditions</a> and{" "}
                  <a href="/legal-information" style={S.inlineLink}>Privacy Policy</a>
                </span>
              </label>
            </div>

            <div style={S.reviewRight}>
              <div style={S.priceCard}>
                <img src={HERO_IMAGE} alt="Bedroom & Wardrobe Cleaning" style={S.priceCardImg} />
                <div style={S.priceCardTitle}>{categoryMeta?.label} · {areaMeta?.label}</div>

                <div style={S.billRow}><span>Base Cleaning</span><span>{fmt(basePrice)}</span></div>
                <div style={S.billDivider} />
                <div style={S.billRow}><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                <div style={S.billRow}><span>GST ({CHARGES.GST_PERCENT}%)</span><span>{fmt(gst)}</span></div>
                <div style={S.billDivider} />
                <div style={S.totalRow}><span>Total Amount</span><span>{fmt(total)}</span></div>

                <button type="button" style={{ ...S.continueBtn, ...((!agreedToTerms || !selectedAddressId || placing) ? S.continueBtnDisabled : {}) }}
                  disabled={!agreedToTerms || !selectedAddressId || placing} onClick={handlePlaceOrder}>
                  {placing ? "Placing Order…" : paymentOption === "pay_now" ? "Proceed to Payment" : "Confirm Booking"}
                  {!placing && <ArrowRight size={16} />}
                </button>
                {!selectedAddressId && (
                  <div style={{ ...S.recapSubText, color: "#7c3aed", justifyContent: "center", marginBottom: "10px" }}>
                    Select or add a service address above to continue
                  </div>
                )}
                <div style={S.secureNote}><ShieldCheck size={13} color="#7c3aed" /> Secure checkout • Verified professionals • No hidden charges</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLES — violet (#7c3aed) accent, distinct from the other special-service
// wizards' colors on the Special Services listing page.
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1100px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px" },
  breadcrumbLink: { color: "#9ca3af", textDecoration: "none" },

  hero:      { textAlign: "center", marginBottom: "28px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#ede9fe", color: "#7c3aed", fontSize: "12px", fontWeight: 800, letterSpacing: "0.05em", padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: "0 0 8px", fontSize: "32px", fontWeight: 900 },
  heroText:  { margin: 0, fontSize: "15px", color: "#6b7280" },

  stepRowOuter: { marginBottom: "24px" },
  stepRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", overflowX: "auto", padding: "4px 2px", flexWrap: "nowrap" },
  stepItem: { display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 },
  stepCircle: { width: "26px", height: "26px", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: "11px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  stepCircleActive: { background: "#7c3aed", color: "#fff" },
  stepCircleDone:   { background: "#ede9fe", color: "#7c3aed" },
  stepLabel: { fontSize: "12px", fontWeight: 700, color: "#9ca3af", whiteSpace: "nowrap" },
  stepLabelActive: { color: "#111827" },
  stepSub: { fontSize: "12px", color: "#9ca3af", textAlign: "center", marginTop: "10px" },
  stepConnector: { width: "20px", height: "2px", background: "#e5e7eb", flexShrink: 0 },
  stepConnectorActive: { background: "#7c3aed" },

  backBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#7c3aed", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "16px", padding: 0 },

  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "26px", maxWidth: "760px", margin: "0 auto" },
  sectionTitle: { margin: "0 0 20px", fontSize: "22px", fontWeight: 800 },
  scheduleHeadSub: { margin: "-14px 0 20px", fontSize: "13px", color: "#6b7280" },

  fieldGroup: { marginBottom: "22px" },
  fieldLabel: { fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.03em" },

  radioGrid3: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: "10px" },
  radioCard: { display: "flex", alignItems: "center", gap: "8px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", fontSize: "13px", fontWeight: 600, color: "#374151", cursor: "pointer", textAlign: "left" },
  radioCardActive: { borderColor: "#7c3aed", background: "#ede9fe", color: "#5b21b6" },

  select: { width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontSize: "14px", background: "#fff", color: "#111827" },
  input: { width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontSize: "14px", marginBottom: "10px", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontSize: "14px", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" },
  formRow2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" },

  continueBtn: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#7c3aed", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  continueBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },

  sizeGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "14px", marginBottom: "22px" },
  sizeCard: { textAlign: "left", border: "1.5px solid #e5e7eb", borderRadius: "14px", padding: "16px", background: "#fff", cursor: "pointer" },
  sizeCardActive: { borderColor: "#7c3aed", boxShadow: "0 0 0 3px #ede9fe" },
  sizeCardHead: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" },
  sizeCardLabel: { fontSize: "14px", fontWeight: 700, color: "#111827" },
  sizeCardPrice: { fontSize: "20px", fontWeight: 900, color: "#7c3aed" },

  trustStrip: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "10px", marginBottom: "22px" },
  trustItem: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 600, color: "#4b5563", background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "10px", padding: "10px 12px" },

  doDontGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "20px", marginBottom: "20px" },
  doDontColTitleInclude: { display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 800, color: "#7c3aed", marginBottom: "10px" },
  doDontColTitleExclude: { display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 800, color: "#dc2626", marginBottom: "10px" },
  doDontList: { margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "8px", listStyle: "none" },
  doDontItem: { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "13px", color: "#374151", lineHeight: 1.5 },
  notesBox: { background: "#f8fbff", border: "1px solid #e7eef9", borderRadius: "14px", padding: "16px", marginBottom: "16px" },
  notesBoxTitle: { display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 800, color: "#111827", marginBottom: "10px" },
  notesList: { margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#374151", lineHeight: 1.5 },
  notesItem: { paddingLeft: "2px" },

  chipRow: { display: "flex", flexWrap: "wrap", gap: "8px" },
  dateChip: { display: "inline-flex", alignItems: "center", gap: "6px", padding: "10px 16px", borderRadius: "999px", border: "1.5px solid #e5e7eb", background: "#fff", fontSize: "13px", fontWeight: 700, color: "#374151", cursor: "pointer" },
  dateChipActive: { borderColor: "#7c3aed", background: "#7c3aed", color: "#fff" },
  dateInput: { marginTop: "10px", padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #e5e7eb", fontSize: "13px" },

  slotGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: "10px" },
  slotChip: { padding: "14px 0", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer", textAlign: "center" },
  slotChipActive: { borderColor: "#7c3aed", background: "#7c3aed", color: "#fff" },
  slotChipLabel: { fontSize: "13px", fontWeight: 700, color: "inherit" },
  durationBanner: { display: "flex", alignItems: "center", gap: "6px", background: "#f5f3ff", border: "1px solid #ddd6fe", color: "#7c3aed", fontSize: "13px", fontWeight: 600, padding: "10px 14px", borderRadius: "10px", marginBottom: "16px" },

  locationBtn: { display: "inline-flex", alignItems: "center", gap: "8px", padding: "12px 18px", borderRadius: "12px", border: "1.5px solid #7c3aed", background: "#f5f3ff", color: "#7c3aed", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "10px" },

  addressList: { display: "flex", flexDirection: "column", gap: "10px" },
  addressRow: { display: "flex", alignItems: "center", gap: "10px", padding: "14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  addressRowActive: { borderColor: "#7c3aed", background: "#f5f3ff" },
  addressIcon: { fontSize: "18px" },
  addressBody: { flex: 1 },
  addressLabel: { fontSize: "13px", fontWeight: 700, color: "#111827" },
  addressText: { fontSize: "12px", color: "#6b7280" },

  addAddressBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "1.5px dashed #c4b5fd", color: "#7c3aed", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "12px 16px", borderRadius: "12px", width: "100%", justifyContent: "center", margin: "12px 0 22px" },
  addAddressForm: { border: "1.5px solid #e5e7eb", borderRadius: "14px", padding: "16px", marginBottom: "22px" },
  formActions: { display: "flex", gap: "10px", justifyContent: "flex-end" },
  secondaryBtn: { padding: "10px 16px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 700, fontSize: "13px", cursor: "pointer" },
  primaryBtnSm: { padding: "10px 18px", borderRadius: "10px", border: "none", background: "#7c3aed", color: "#fff", fontWeight: 700, fontSize: "13px", cursor: "pointer" },
  errorText: { fontSize: "12px", color: "#dc2626", marginBottom: "10px" },
  errorBox: { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: "13px", padding: "12px 14px", borderRadius: "10px", marginBottom: "16px" },
  stateBox: { fontSize: "13px", color: "#6b7280", background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "10px", padding: "12px 14px", marginBottom: "16px" },
  inlineLink: { color: "#7c3aed", fontWeight: 700, textDecoration: "none" },

  reviewLayout: { display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: "22px", alignItems: "start" },
  reviewLeft: { display: "flex", flexDirection: "column", gap: "14px" },
  reviewRight: { position: "sticky", top: "20px" },

  recapCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px 18px" },
  recapHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" },
  recapTitle: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 800, color: "#111827" },
  changeLink: { display: "inline-flex", alignItems: "center", gap: "4px", background: "none", border: "none", color: "#7c3aed", fontWeight: 700, fontSize: "12px", cursor: "pointer", padding: 0 },
  recapText: { fontSize: "13px", color: "#374151", lineHeight: 1.5 },
  recapSubText: { display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#6b7280", marginTop: "6px" },

  paymentOptions: { display: "flex", flexDirection: "column", gap: "10px" },
  payOptionBtn: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  payOptionBtnActive: { borderColor: "#7c3aed", background: "#f5f3ff" },
  payOptionBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  payOptionTitle: { fontSize: "13px", fontWeight: 700, color: "#111827" },
  payOptionSub: { fontSize: "11px", color: "#6b7280" },

  termsWrap: { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "12px", color: "#6b7280", lineHeight: 1.5 },

  priceCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px", overflow: "hidden" },
  priceCardImg: { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: "12px", marginBottom: "12px" },
  priceCardTitle: { fontSize: "15px", fontWeight: 800, marginBottom: "12px" },
  billRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#374151", marginBottom: "10px" },
  billDivider: { borderTop: "1px solid #e5e7eb", margin: "10px 0" },
  totalRow: { display: "flex", justifyContent: "space-between", fontSize: "18px", fontWeight: 900, marginBottom: "16px" },
  secureNote: { display: "flex", alignItems: "center", gap: "6px", justifyContent: "center", marginTop: "12px", fontSize: "11px", color: "#6b7280", textAlign: "center" },

  // Confirmation screen
  confirmCard: { position: "relative", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "24px", padding: "40px 28px", textAlign: "center", overflow: "hidden" },
  confettiWrap: { position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" },
  confettiPiece: { position: "absolute", top: "-10px", width: "8px", height: "8px", borderRadius: "2px", animationName: "bedroomWardrobeConfettiFall", animationTimingFunction: "ease-in", animationFillMode: "forwards" },
  confirmCheckWrap: { display: "flex", justifyContent: "center", marginBottom: "18px" },
  confirmCheck: { width: "84px", height: "84px", borderRadius: "50%", background: "linear-gradient(135deg,#22c55e,#16a34a)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 30px rgba(34,197,94,0.35)" },
  confirmTitle: { margin: "0 0 6px", fontSize: "24px", fontWeight: 900, color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  confirmSub: { margin: "0 0 14px", fontSize: "15px", color: "#374151", fontWeight: 600 },
  confirmOrderId: { display: "inline-block", background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "999px", padding: "8px 16px", fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "18px" },
  confirmDetailRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontSize: "13px", color: "#4b5563", marginBottom: "8px" },
  confirmNote: { margin: "16px 0 22px", fontSize: "12.5px", color: "#9ca3af" },
  confirmActions: { display: "flex", flexDirection: "column", gap: "10px" },
  confirmPrimaryBtn: { width: "100%", padding: "15px 0", borderRadius: "12px", background: "#7c3aed", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  confirmSecondaryBtn: { display: "inline-block", padding: "13px 0", borderRadius: "12px", border: "1.5px solid #e5e7eb", color: "#374151", fontWeight: 700, fontSize: "14px", textDecoration: "none" },
};

// Keyframes for the confetti burst (can't be declared inline via the style
// object above — injected once as a real stylesheet rule).
if (typeof document !== "undefined" && !document.getElementById("bedroom-wardrobe-confetti-keyframes")) {
  const styleTag = document.createElement("style");
  styleTag.id = "bedroom-wardrobe-confetti-keyframes";
  styleTag.innerHTML = `
    @keyframes bedroomWardrobeConfettiFall {
      0%   { transform: translateY(0) rotate(0deg); opacity: 1; }
      100% { transform: translateY(340px) rotate(360deg); opacity: 0; }
    }
  `;
  document.head.appendChild(styleTag);
}

export default BedroomWardrobeCleaningPage;
